// Slides route — renders the whiteboard shell in slides mode.
// The whiteboard components determine slides mode by reading `?slides=true`
// from query params, so we ensure it's present before the tree mounts.
// Once slides has its own rendering path, the query-param bridge can be removed.

import React, { useContext, useLayoutEffect } from 'react';

import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { SLIDES_QUERY_PARAM } from '@confluence/whiteboard-utils/entry-points/whiteboardConstants.ts';
import { CONTAINER_HOME_ID } from '@confluence/app-navigation/entry-points/navigationIDs.ts';

import { WhiteboardRouteComponentLoader } from './WhiteboardRouteComponentLoader.tsx';

export const SlidesRoute = (match: RouteMatch) => {
	const {
		params: { contentId, spaceKey },
	} = match;

	const { getQueryParams, setQueryParams } = useContext(RoutesContext);

	// Ensure ?slides=true is present so downstream whiteboard components
	// render in slides mode. Replace (don't push) to avoid polluting history.
	useLayoutEffect(() => {
		if (getQueryParams()?.[SLIDES_QUERY_PARAM] !== 'true') {
			setQueryParams({ [SLIDES_QUERY_PARAM]: 'true' }, true);
		}
	}, [getQueryParams, setQueryParams]);

	return <WhiteboardRouteComponentLoader spaceKey={spaceKey} contentId={contentId} />;
};

SlidesRoute.NAVIGATION_PARAMS = ({
	name: routeName,
	params: { contentId, spaceKey },
	query: { focusedCommentId, focusedMentionId },
}: RouteMatch) => ({
	Screen: {
		screenEvent: {
			name: 'slideScreen',
			id: contentId,
			attributes: {
				contentId,
				focusedCommentId,
				focusedMentionId,
			},
		},
		pageState: {
			contentId,
			spaceKey,
			contentType: 'slide',
			routeName,
		},
	},
	MainLayout: { navView: CONTAINER_HOME_ID, spaceKey, isViewPage: true },
});
